import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { ConfigService } from '../services/config.service';
import { BusinessContextService } from '../services/business-context.service';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { BusinessSelectDialogComponent, BusinessInfoItem } from './business-select-dialog.component';

// Standalone functional guard using DI-friendly inject()
export const AuthGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const cfg = inject(ConfigService);
  const keycloak = inject(KeycloakService);
  const router = inject(Router);
  const bizCtx = inject(BusinessContextService);
  const http = inject(HttpClient);
  const dialog = inject(MatDialog);
  const httpBypass = new HttpClient(inject(HttpBackend));

  // If Keycloak is not configured, skip auth (useful for local/dev without SSO)
  const enabled = !!(cfg as any).keycloakUrl && !!(cfg as any).keycloakRealm && !!(cfg as any).keycloakClientId;
  if (!enabled || !keycloak) {
    return true;
  }

  const loggedIn = await keycloak.isLoggedIn();
  if (!loggedIn) {
    // Permitir bypass en desarrollo solo para rutas no administrativas
    const isAdminRoute = state.url?.startsWith('/admin');
    if (cfg.isDevelopment && !isAdminRoute) {
      console.warn('[AuthGuard] Not logged in, proceeding due to isDevelopment=true and non-admin route');
      return true;
    }
    // Redirigir al login personalizado en rutas protegidas
    await router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  // After successful login ensure business context is available
  try {
    const apiUrl = (cfg as any).apiUrl as string;
    if (apiUrl) {
      // ALWAYS call /auth/me once after login to hydrate context and names
      const me: any = await http.get(`${apiUrl}/auth/me?v=${Date.now()}`).toPromise();
      const businesses = Array.isArray(me?.businesses) ? me.businesses : [];
      const enabledBusinesses = businesses.filter((b: any) => !('enabled' in b) || b.enabled === true);
      const legacyList: string[] = Array.isArray(me?.businessUuids) ? me.businessUuids.map((u: any) => (u || '').toString()) : [];
      const list: string[] = (enabledBusinesses.length > 0
        ? enabledBusinesses.map((b: any) => (b?.uuid || '').toString())
        : legacyList).filter((s: string) => !!s);
      const active = (me?.activeBusinessUuid || '').toString();

      const existingSelected = bizCtx.getSelected();
      const existingName = bizCtx.getSelectedName();
      if (existingSelected) {
        // Ensure name is stored and backend active is confirmed
        const nameFromEnabled = enabledBusinesses.map((b: any) => ({ uuid: (b?.uuid || '').toString(), name: (b?.name || '').toString() }))
          .find((i: any) => i.uuid === existingSelected)?.name || existingName || null;
        bizCtx.setSelectedWithName(existingSelected, nameFromEnabled);
      } else {
        // No selection yet: decide and possibly open dialog if multiple
        const parsed: any = keycloak.getKeycloakInstance?.()?.tokenParsed || {};
        const tokenBusinessList: string[] = Array.isArray(parsed?.businessUuids) ? parsed.businessUuids.map((u: any) => (u || '').toString()) : [];

        if ((list.length > 1) || (list.length === 0 && tokenBusinessList.length > 1)) {
          try {
            const infos: BusinessInfoItem[] = enabledBusinesses.map((b: any): BusinessInfoItem => ({ uuid: (b?.uuid || '').toString(), name: (b?.name || '').toString() || (b?.uuid || '').toString() }));
            const infosFallback: BusinessInfoItem[] = (list.length > 0 ? list : tokenBusinessList).map((uuid: string): BusinessInfoItem => ({ uuid, name: uuid }));
            const items: BusinessInfoItem[] = (infos.length > 0 ? infos : infosFallback);

            const chosenUuid: string | null = await new Promise((resolve) => {
              const ref = dialog.open(BusinessSelectDialogComponent, {
                width: '460px',
                data: { items }
              });
              ref.afterClosed().subscribe((val: string | null) => resolve(val || null));
            });

            if (chosenUuid) {
              const chosenName = items.find((i: BusinessInfoItem) => i.uuid === chosenUuid)?.name || null;
              bizCtx.setSelectedWithName(chosenUuid, chosenName);
            } else {
              const fallback = active || list[0] || tokenBusinessList[0] || null;
              if (fallback) {
                const fallbackName = items.find((i: BusinessInfoItem) => i.uuid === fallback)?.name || null;
                bizCtx.setSelectedWithName(fallback, fallbackName);
              }
            }
          } catch {
            const chosen = active || list[0] || null;
            if (chosen) {
              bizCtx.setSelectedWithName(chosen, null);
            }
          }
        } else {
          const chosen = active || list[0] || null;
          if (chosen) {
            const nameFromEnabled = enabledBusinesses.map((b: any) => ({ uuid: (b?.uuid || '').toString(), name: (b?.name || '').toString() }))
              .find((i: any) => i.uuid === chosen)?.name || null;
            bizCtx.setSelectedWithName(chosen, nameFromEnabled);
          }
        }
      }
    }
    // Fallback to token claims if backend returns nothing
    if (!bizCtx.getSelected()) await bizCtx.syncFromToken(keycloak);
  } catch {
    // If /auth/me fails, fallback to token claims
    try { await bizCtx.syncFromToken(keycloak); } catch {}
  }

  // Role-based access control: if route defines required roles, enforce them
  try {
    const required: string[] = (route?.data && Array.isArray(route.data['roles'])) ? route.data['roles'] : [];
    if (required.length > 0) {
      const userRoles = (keycloak.getUserRoles ? keycloak.getUserRoles(true) : []) as string[];
      const userSet = new Set(userRoles.map(r => (r || '').toLowerCase()));
      const ok = required.some(r => userSet.has((r || '').toLowerCase()));
      if (!ok) {
        // Not authorized: go to a safe default
        await router.navigate(['/admin']);
        return false;
      }
    }
  } catch {}

  return true;
};
